import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Search, PackageOpen, MessageCircle } from "lucide-react";
import api, { waLink } from "../lib/api";
import ProductCard from "./ProductCard";
import { CATEGORIES } from "../lib/images";

const reveal = (delay) => ({
    initial: { opacity: 0, y: 36 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.15 },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Catalog() {
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [category, setCategory] = useState("Semua Karya");

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), 300);
        return () => clearTimeout(t);
    }, [search]);

    const { data: products, isLoading } = useQuery({
        queryKey: ["products", debounced, category],
        queryFn: async () => (await api.get("/products", { params: { search: debounced, category } })).data,
    });

    const { data: kategoris } = useQuery({
        queryKey: ["categories"],
        queryFn: async () => (await api.get("/categories")).data,
    });
    const daftarKategori = ["Semua Karya", ...(kategoris?.length ? kategoris.map((c) => c.name) : CATEGORIES.filter((c) => c !== "Semua Karya"))];

    return (
        <section id="katalog" className="scroll-mt-24 py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div {...reveal(0)} className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-2xl">
                        <span className="inline-block px-3 py-1 rounded-full border-2 border-brand-ink bg-white text-[11px] font-extrabold uppercase tracking-[0.18em] shadow-hard-sm">Katalog</span>
                        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
                            Karya Dari <span className="text-brand-forest">Sampah Kertas</span>
                        </h2>
                        <p className="mt-3 text-brand-ink/70">Setiap piece dikerjakan manual oleh kreator muda — satu-satu, tidak massal.</p>
                    </div>
                    <div className="relative w-full sm:w-80">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-brand-ink/40" />
                        <input
                            data-testid="catalog-search-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cari karya, bahan, atau nama kreator..."
                            className="w-full pl-11 pr-4 py-3.5 rounded-full border-2 border-brand-ink bg-white text-sm font-semibold placeholder:text-brand-ink/40 placeholder:font-medium focus:outline-none focus:ring-4 focus:ring-brand-sunshine/60 shadow-hard-sm"
                        />
                    </div>
                </motion.div>

                <motion.div {...reveal(0.1)} className="mt-8 flex flex-wrap gap-2.5">
                    {daftarKategori.map((c) => (
                        <button
                            key={c}
                            data-testid="catalog-category-filter"
                            data-category={c}
                            onClick={() => setCategory(c)}
                            className={`px-4 py-2.5 rounded-full border-2 border-brand-ink text-sm font-bold shadow-hard-sm transition-all active:translate-y-0.5 active:shadow-none ${
                                category === c ? "bg-brand-ink text-brand-cream" : "bg-white hover:bg-brand-sunshine/60"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </motion.div>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 items-stretch">
                    {isLoading
                        ? Array.from({ length: 8 }).map((_, i) => (
                              <div key={i} className="rounded-[26px] border-2 border-brand-ink bg-white overflow-hidden shadow-hard-sm">
                                  <div className="aspect-square bg-brand-ink/10 animate-pulse" />
                                  <div className="p-5 space-y-3">
                                      <div className="h-4 bg-brand-ink/10 rounded animate-pulse w-3/4" />
                                      <div className="h-3 bg-brand-ink/10 rounded animate-pulse w-1/2" />
                                  </div>
                              </div>
                          ))
                        : products?.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}

                    {!isLoading && products && products.length === 0 && (
                        <div className="col-span-full py-16 rounded-[26px] border-2 border-dashed border-brand-ink/40 bg-white/70 text-center px-6">
                            <PackageOpen className="w-10 h-10 mx-auto text-brand-ink/50" />
                            <p className="mt-4 font-display text-xl font-bold">Belum ada karya yang cocok</p>
                            <p className="mt-1.5 text-sm text-brand-ink/60">Coba kata kunci lain atau kategori yang berbeda ya.</p>
                            <a
                                data-testid="catalog-empty-wa-button"
                                href={waLink("Halo Acture.id! Saya mau tanya soal karya di katalog.")}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full border-2 border-brand-ink bg-brand-wa text-white font-display font-bold shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0"
                            >
                                <MessageCircle className="w-4 h-4" strokeWidth={2.6} /> Tanya via WhatsApp
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}
